import LogRocket from 'logrocket';
import type { User } from '../api/types';

const BLOCKED_IPS = new Set<string>(['14.143.173.26', '183.82.113.48']);
const LOGROCKET_ENABLE_TOKENS = new Set(['true', '1', 'yes', 'on', 'enabled']);
const LOGROCKET_DISABLE_TOKENS = new Set(['false', '0', 'no', 'off', 'disabled']);

// ---------------------------------------------------------------------------
// PII field-level masking
// ---------------------------------------------------------------------------

/** Lowercased PII field names — matched case-insensitively in JSON bodies. */
const PII_FIELDS = new Set([
  // Titles
  'title', 'customertitle', 'debtor1title', 'debtor2title', 'partnertitle',
  // Identity — generic
  'firstname', 'lastname', 'surname', 'name', 'fullname',
  // Identity — debtor1 / debtor2 / debtor (no index) variants
  'debtorname', 'debtor1name', 'debtor2name',
  'debtor1firstname', 'debtor1lastname', 'debtor1surname',
  'debtor2firstname', 'debtor2lastname', 'debtor2surname',
  // Identity — partner
  'partnerfirstname', 'partnerlastname', 'partnersurname', 'partnername',
  // Identity — account display
  'displayname', 'username', 'userid',
  // Contact
  'email', 'emailid', 'emailaddress',
  'phonenumber', 'mobilenumber', 'landlinenumber', 'phone',
  // Address
  'addressline1', 'addressline2', 'buildingnumber', 'buildingname',
  'streetname', 'posttown', 'city', 'postcode', 'postcodetrimmed', 'address',
  // DOB — generic + debtor + partner + dependent + inbest variants
  'dateofbirth', 'dob', 'userdob',
  'debtor1dateofbirth', 'debtor1dateofbirthstr', 'debtor1dob',
  'debtor2dateofbirth', 'debtor2dateofbirthstr', 'debtor2dob',
  'partnerdateofbirth', 'partnerdateofbirthstr', 'partnerdob',
  'childdependantdob', 'childdependantdobstr',
  // Financial identifiers
  'accountnumber', 'referencenumber', 'companyname',
  // Auth
  'password', 'currentpassword', 'newpassword', 'confirmpassword',
  'token', 'refreshtoken',
  // Free text (may contain PII)
  'message', 'notes',
]);

/** Recursively replace PII field values with '***'. */
function maskPiiFields(obj: unknown, depth = 0): unknown {
  if (depth > 10 || obj === null || obj === undefined) return obj;
  if (typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map((item) => maskPiiFields(item, depth + 1));

  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
    result[key] = PII_FIELDS.has(key.toLowerCase()) ? '***' : maskPiiFields(value, depth + 1);
  }
  return result;
}

/** Parse JSON body, mask PII fields, re-serialize. Non-JSON bodies are stripped. */
function sanitizeBody(body: string | undefined): string {
  if (!body) return '';
  try {
    return JSON.stringify(maskPiiFields(JSON.parse(body)));
  } catch {
    return '';
  }
}

// ---------------------------------------------------------------------------
// URL classification
// ---------------------------------------------------------------------------

/** Endpoints where PII appears in the URL path — fully blocked. */
const BLOCKED_URL_PATTERNS = [
  'ie-post_code_list_vs_client_by-pcode/',
  'ie-postcodes-by-country/',
  'customer-shared-doc-preview/',
];

/** Payment-offer with path params (accountNumber in URL). */
const PAYMENT_OFFER_PATH_PATTERN =
  /ie-customer-payment-offer\/(customer\/[^?]+\/lender\/[^?]+\/account\/[^?]+|[^?]+\/[^?]+\/)/i;

/** Master data / lookup endpoints — no customer PII, allow fully. */
const SAFE_URL_SUBSTRINGS = [
  'ie-mstr-',
  'account-type-codes',
  '/lookups',
  'ie-income-triggers',
  'ie-expenses-triggers-api',
  'ie-guidance-notes',
  'show-additional-support-companies',
  'ie-event-log',
  'customer-declarations',
];

/** URL substrings that should always be masked (bodies contain PII). */
const MASK_URL_SUBSTRINGS = [
  '/iehub-customer/',
  '/api/auth/',
  '/api/oauth/',
  'inbest/calculate',
  'terms-and-conditions-accepted',
];

type SanitizeAction = 'block' | 'allow' | 'mask';

function classifyUrl(url: string): SanitizeAction {
  const lower = url.toLowerCase();

  // Fully block — PII embedded in URL path
  if (BLOCKED_URL_PATTERNS.some((p) => lower.includes(p))) return 'block';
  if (PAYMENT_OFFER_PATH_PATTERN.test(url)) return 'block';

  // Safe master-data endpoints — allow with full body
  if (SAFE_URL_SUBSTRINGS.some((p) => lower.includes(p))) return 'allow';

  // Endpoints that carry customer PII — mask field values in body
  if (MASK_URL_SUBSTRINGS.some((p) => lower.includes(p))) return 'mask';

  // External / static assets — pass through
  return 'allow';
}

// ---------------------------------------------------------------------------
// Initialisation
// ---------------------------------------------------------------------------

function isLogRocketEnabled(): boolean {
  // Default ON (legacy parity): only disable when explicitly set to a false token.
  const rawFlag = import.meta.env.VITE_ENABLE_LOGROCKET;
  if (rawFlag === undefined) return true;
  const normalized = String(rawFlag).trim().toLowerCase();
  if (LOGROCKET_DISABLE_TOKENS.has(normalized)) return false;
  if (LOGROCKET_ENABLE_TOKENS.has(normalized)) return true;
  // Any other value: keep enabled (opt-out only)
  return true;
}

async function getPublicIP(): Promise<string | null> {
  try {
    const resp = await fetch('https://www.cloudflare.com/cdn-cgi/trace');
    const text = await resp.text();
    const map = text
      .trim()
      .split('\n')
      .reduce<Record<string, string>>((acc, line) => {
        const [k, v] = line.split('=');
        if (k && v) acc[k] = v;
        return acc;
      }, {});
    return map.ip || null;
  } catch {
    return null;
  }
}

export async function initLogRocket(): Promise<void> {
  try {
    // Explicit true/false control through VITE_ENABLE_LOGROCKET
    if (!isLogRocketEnabled()) return;

    // Legacy parity: apply IP gate in production (avoids blocking local/dev and avoids extra network hops).
    if (import.meta.env.PROD) {
      const ip = await getPublicIP();
      if (ip && BLOCKED_IPS.has(ip)) return;
    }

    LogRocket.init('p8rrlg/iehub', {
      dom: {
        // inputSanitizer: false allows input values to be captured
        // BUT fields with data-private attribute are automatically masked by LogRocket
        // All TextField, SpecialTextField, Select, DateField, FreeSelect components
        // have dataPrivate=true by default to protect PII
        inputSanitizer: false,
      },
      network: {
        requestSanitizer: (request: { reqId: string; url: string; headers: Record<string, string | undefined>; body?: string; method: string }) => {
          const action = classifyUrl(request.url);
          if (action === 'block') return null;
          if (action === 'mask') {
            if (request.headers?.Authorization) request.headers.Authorization = '***';
            if (request.headers?.authorization) request.headers.authorization = '***';
            request.body = sanitizeBody(request.body);
          }
          return request;
        },
        responseSanitizer: (response: { reqId: string; status?: number; headers: Record<string, string | undefined>; body?: string; method: string; url?: string }) => {
          const action = classifyUrl(response.url ?? '');
          if (action === 'block') return null;
          if (action === 'mask') {
            response.body = sanitizeBody(response.body);
          }
          return response;
        },
      },
    });

    // Mirror legacy behavior: silence console.log in production only
    if (import.meta.env.PROD) {
      // eslint-disable-next-line @typescript-eslint/no-empty-function, no-console
      console.log = function () {};
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('LogRocket init failed', e);
  }
}

export function identifyUser(user: Pick<User, 'userId'> | null | undefined): void {
  try {
    if (!user || !user.userId) return;
    LogRocket.identify(String(user.userId), {
      userId: user.userId,
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('LogRocket identify failed', e);
  }
}
